import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiBook, FiSearch, FiChevronRight, FiPlus } from "react-icons/fi";
import { useAuth } from "../../context/AuthContext";
import documentacoesService, { Documentacao } from "../../services/documentacoes";
import Skeleton from "../../components/ui/Skeleton";
import DocumentacaoEditModal from "../../components/documentacoes/DocumentacaoEditModal";
import AuthenticatedImage from "../../components/ui/AuthenticatedImage";

function Documentacoes() {
  const navigate = useNavigate();
  const { getUser } = useAuth();
  const user = getUser();
  const isAdmin = !!user?.is_admin;
  const [loading, setLoading] = useState(false);
  const [documentacoes, setDocumentacoes] = useState<Documentacao[]>([]);
  const [search, setSearch] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    loadDocumentacoes();
  }, []);

  const loadDocumentacoes = async () => {
    setLoading(true);
    try {
      const data = await documentacoesService.list();
      setDocumentacoes(data);
    } catch (error) {
      console.error("Erro ao carregar documentações:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredDocumentacoes = documentacoes.filter((doc) =>
    doc.title.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Função para extrair a primeira imagem do markdown
  const getFirstImageFromMarkdown = (markdown: string): string | null => {
    if (!markdown) return null;

    // Regex para encontrar imagens no formato ![alt](url) ou ![alt](url "title")
    const imageRegex = /!\[([^\]]*)\]\(([^)]+)\)/;
    const match = imageRegex.exec(markdown);

    if (match?.[2]) {
      return match[2].trim().replaceAll(/(^"|"$)/g, ""); // Remove aspas se houver
    }

    return null;
  };

  return (
    <div className="mx-auto max-w-7xl px-3 sm:px-4 py-4 sm:py-6 md:px-6 2xl:px-10">
      {/* Header Premium */}
      <div className="mb-4 sm:mb-6">
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 via-purple-600 to-purple-700 p-4 sm:p-6 shadow-lg">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-3xl"></div>
          <div className="absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-white/10 blur-3xl"></div>

          <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="rounded-xl bg-white/20 p-2.5 sm:p-3 backdrop-blur-sm">
                <FiBook className="text-white" size={24} />
              </div>
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white">
                  Manual do Sistema
                </h2>
                <p className="mt-1 text-xs sm:text-sm text-purple-100">
                  Documentações e guias do sistema
                </p>
              </div>
            </div>
            {isAdmin && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-semibold text-purple-600 shadow-lg transition-all hover:scale-105 hover:shadow-xl active:scale-95 touch-manipulation"
              >
                <FiPlus size={20} />
                <span className="hidden sm:inline">Nova Documentação</span>
                <span className="sm:hidden">Nova</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Card Principal */}
      <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark overflow-hidden">
        {/* Header do Card com Search */}
        <div className="border-b border-stroke bg-gray-50 px-3 sm:px-6 py-4 sm:py-5 dark:border-strokedark dark:bg-gray-900/20">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <div className="h-1 w-6 sm:w-8 rounded bg-purple-600"></div>
              <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
                Documentações
              </h3>
            </div>

            <div className="relative w-full sm:w-auto sm:max-w-xs">
              <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2">
                <FiSearch className="text-gray-400" size={18} />
              </span>
              <input
                type="text"
                placeholder="Buscar documentação..."
                value={search}
                onChange={({ target }) => setSearch(target.value)}
                className="w-full rounded-lg sm:rounded-xl border border-stroke bg-white py-2 sm:py-2.5 pl-10 sm:pl-12 pr-3 sm:pr-4 text-sm sm:text-base outline-none transition focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation"
              />
            </div>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="p-3 sm:p-6">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-lg sm:rounded-xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark p-4"
                >
                  <Skeleton className="mb-2 h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="mt-2 h-4 w-1/4" />
                </div>
              ))}
            </div>
          ) : filteredDocumentacoes.length > 0 ? (
            <div className="space-y-4">
              {filteredDocumentacoes.map((doc) => {
                const thumbnailUrl = getFirstImageFromMarkdown(doc.text);
                const hasThumbnail = !!thumbnailUrl;

                return (
                  <div
                    key={doc.id}
                    onClick={() => navigate(`/documentacoes/${doc.id}`)}
                    role="button"
                    tabIndex={0}
                    className="group cursor-pointer overflow-hidden rounded-lg sm:rounded-xl border border-stroke bg-white shadow-sm transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-95 dark:border-strokedark dark:bg-boxdark touch-manipulation"
                  >
                    <div className={`flex ${hasThumbnail ? "flex-row" : ""}`}>
                      {/* Thumbnail */}
                      {hasThumbnail && (
                        <div className="w-32 sm:w-40 flex-shrink-0 overflow-hidden bg-gray-100 dark:bg-gray-800">
                          {thumbnailUrl.startsWith("http://") ||
                          thumbnailUrl.startsWith("https://") ? (
                            <img
                              src={thumbnailUrl}
                              alt={doc.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <AuthenticatedImage
                              fileName={thumbnailUrl}
                              alt={doc.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          )}
                        </div>
                      )}

                      {/* Conteúdo */}
                      <div className="flex-1 p-4 sm:p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors mb-2">
                              {doc.title}
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                              Atualizado em {formatDate(doc.updated_at)}
                            </p>
                          </div>
                          <div className="flex items-center text-purple-600 dark:text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">
                            <FiChevronRight size={20} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 sm:py-16">
              <div className="mb-4 sm:mb-6 rounded-2xl bg-gradient-to-br from-purple-50 to-purple-100 p-4 sm:p-6 dark:from-purple-900/20 dark:to-purple-800/20">
                <FiBook
                  size={40}
                  className="text-purple-600 dark:text-purple-400 sm:w-12 sm:h-12"
                />
              </div>
              <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {search
                  ? "Nenhuma documentação encontrada"
                  : "Nenhuma documentação disponível"}
              </p>
              <p className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center max-w-md px-4">
                {search
                  ? "Tente buscar por outro termo"
                  : "As documentações aparecerão aqui quando forem adicionadas"}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Criação */}
      {isAdmin && (
        <DocumentacaoEditModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          documentacao={null}
          onSuccess={() => {
            loadDocumentacoes();
            setIsCreateModalOpen(false);
          }}
        />
      )}
    </div>
  );
}

export default Documentacoes;
